import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { usePerfil } from '../context/PerfilContext';
import { ModalPlantillaCategorias } from '../components/ModalPlantillaCategorias';
import { AsistenteFinanzas } from '../components/AsistenteFinanzas';
import { GuiaApplePay } from '../components/GuiaApplePay';
import { Novedades } from '../components/Novedades';
import {
  PlusCircle, Wallet, TrendingUp, TrendingDown,
  PiggyBank, AlertTriangle, Clock, ChevronRight, HelpingHand, CheckCheck, Sparkles,
} from 'lucide-react';
import { ModalAgregarTransaccion } from '../components/ModalAgregarTransaccion';
import { GraficoGastos } from '../components/GraficoGastos';
import { isPast, isToday } from 'date-fns';
import { nombreDeUsuario } from '../utils/usuario';
import { AvisosAMedias } from '../components/amedias/AvisosAMedias';
import { computa, pendientePorPersona } from '../utils/transaccionUtils';
import { useConfirmar } from '../context/ConfirmarContext';
import { limiteEnPeriodo } from '../utils/presupuestoUtils';
import { formatoEuros } from '../utils/numeros';
import { AvisoResumenMes } from '../components/informes/AvisoResumenMes';

// ── Helpers ───────────────────────────────────────────────────────────────────

function diasEnMes(anio: number, mes: number) {
  return new Date(anio, mes + 1, 0).getDate();
}

// Cerrado el asistente, no vuelve a saltar solo hasta la próxima vez que se abra
// la app: así no persigue al usuario cada vez que pasa por el resumen.
let asistenteDescartado = false;

export const Dashboard: React.FC = () => {
  const confirmar = useConfirmar();
  const navigate = useNavigate();
  const {
    cuentas, transacciones, categorias,
    presupuestos,
    transaccionesRecurrentes, traspasosRecurrentes,
    loading, saldarAdelantosDe, categoriasCargadas, cuentasCargadas,
  } = useData();
  const { user } = useAuth();
  const { perfil, marcarPlantillaCategoriasVista } = usePerfil();
  const [showModal, setShowModal] = useState(false);

  const ahora = new Date();
  const mesActualStr = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}`;

  // ── Saldo total ─────────────────────────────────────────────────────────────
  const cuentasNormales = cuentas.filter(c => c.tipo !== 'hucha');
  const saldoTotal = cuentasNormales.reduce((t, c) => {
    if (c.tipo === 'ahorro' && c.ocultarSaldo) return t;
    return t + c.saldo;
  }, 0);

  // ── Ingresos / Gastos del mes (excluyendo traspasos y adelantos) ────────────
  const { ingresosMes, gastosMes } = useMemo(() => {
    let ing = 0, gas = 0;
    transacciones.forEach(t => {
      const f = new Date(t.fecha);
      if (f.getMonth() !== ahora.getMonth() || f.getFullYear() !== ahora.getFullYear()) return;
      if (!computa(t)) return;
      if (t.tipo === 'ingreso') ing += t.cantidad;
      if (t.tipo === 'gasto')   gas += t.cantidad;
    });
    return { ingresosMes: ing, gastosMes: gas };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transacciones]);

  const ahorroMes = ingresosMes - gastosMes;

  // ── Adelantos pendientes de devolver ────────────────────────────────────────
  const pendienteAdelantos = useMemo(() => pendientePorPersona(transacciones), [transacciones]);
  const [saldando, setSaldando] = useState<string | null>(null);

  // Cierra las cuentas con una persona cuando el resto ya no se va a mover
  // (céntimos de redondeo, o algo que se da por perdonado).
  const saldar = async (persona: string, importe: number) => {
    const resto = `${formatoEuros(Math.abs(importe))}`;
    const aviso = importe > 0
      ? `${persona} te debe ${resto}. Si lo saldas, das por perdida esa diferencia.`
      : `${persona} te ha dado ${resto} de más. Si lo saldas, te lo quedas.`;
    if (!(await confirmar({
      titulo: `¿Saldar con ${persona}?`,
      mensaje: `${aviso}\n\nSus adelantos quedan cerrados y dejan de contar como pendientes. ` +
        'No se crea ningún movimiento ni cambia el saldo de tus cuentas.',
      accion: 'Saldar',
      destructiva: false,
    }))) return;
    setSaldando(persona);
    try {
      await saldarAdelantosDe(persona);
    } catch (e) {
      console.error(e);
      await confirmar({ titulo: 'No se pudo saldar', mensaje: 'Inténtalo de nuevo.', accion: 'Entendido', soloAviso: true });
    } finally {
      setSaldando(null);
    }
  };

  // ── Primera vez en Finanzas: ofrecer la plantilla de categorías ──────────
  // Solo con las categorías ya leídas (si no, «cargando» parecería «no tiene»)
  // y una única vez: al cerrarla, cree algo o no, queda anotado en el perfil.
  const [plantillaCerrada, setPlantillaCerrada] = useState(false);
  // Abierta a mano, desde el aviso de novedades
  const [verPlantilla, setVerPlantilla] = useState(false);
  // ── Sin ninguna cuenta: el asistente de primeros pasos ───────────────────
  // Sin cuenta no se puede apuntar nada, así que va antes que la plantilla
  // (y ya ofrece las categorías dentro).
  const sinCuentas = cuentasCargadas && cuentas.length === 0;
  const [verAsistente, setVerAsistente] = useState(() => !asistenteDescartado);
  // Abierto a mano desde la Ayuda (/finanzas/dashboard?asistente=1), aunque ya haya cuentas
  const [parametros, setParametros] = useSearchParams();
  const pedidoAMano = parametros.has('asistente');
  // Con las categorías cargadas: el asistente decide al abrirse si pregunta por ellas
  const mostrarAsistente = cuentasCargadas && categoriasCargadas
    && (pedidoAMano || (sinCuentas && verAsistente));
  // Una vez abierto se queda hasta que el usuario lo cierra, aunque ya tenga cuenta
  const [asistenteEnCurso, setAsistenteEnCurso] = useState(false);
  if (mostrarAsistente && !asistenteEnCurso) setAsistenteEnCurso(true);
  useEffect(() => {
    if (asistenteEnCurso && pedidoAMano) setParametros({}, { replace: true });
  }, [asistenteEnCurso, pedidoAMano, setParametros]);

  // Cómo apuntar al pagar con Apple Pay (/finanzas/dashboard?applepay=1, desde la Ayuda o el asistente)
  const [verApplePay, setVerApplePay] = useState(false);
  useEffect(() => {
    if (!parametros.has('applepay')) return;
    setVerApplePay(true);
    setParametros({}, { replace: true });
  }, [parametros, setParametros]);

  const cerrarAsistente = (apuntar = false) => {
    asistenteDescartado = true;
    setVerAsistente(false);
    setAsistenteEnCurso(false);
    if (apuntar) setShowModal(true);
  };

  const ofrecerPlantilla = categoriasCargadas && categorias.length === 0
    && !!perfil && !perfil.plantillaCategoriasVista && !plantillaCerrada
    && cuentasCargadas && !sinCuentas && !asistenteEnCurso;

  const cerrarPlantilla = () => {
    setPlantillaCerrada(true);
    marcarPlantillaCategoriasVista().catch(e => console.error('No se pudo anotar la plantilla como vista', e));
  };

  // ── Proyección fin de mes ────────────────────────────────────────────────────
  const diaHoy = ahora.getDate();
  const totalDiasMes = diasEnMes(ahora.getFullYear(), ahora.getMonth());
  const gastoDiario = diaHoy > 0 ? gastosMes / diaHoy : 0;
  const proyeccionMes = gastoDiario * totalDiasMes;

  // ── Alertas: periódicos vencidos ─────────────────────────────────────────────
  const periodVencidos = useMemo(() => {
    const esVencido = (f: Date) => isPast(new Date(f)) || isToday(new Date(f));
    return [
      ...transaccionesRecurrentes.filter(t => t.activo && esVencido(t.proximaFecha)),
      ...traspasosRecurrentes.filter(t => t.activo && esVencido(t.proximaFecha)),
    ].length;
  }, [transaccionesRecurrentes, traspasosRecurrentes]);

  // ── Alertas: presupuestos excedidos / cerca del límite (≥ 90 %) ─────────────
  const alertasPresupuesto = useMemo(() => {
    // Modelo evergreen: un presupuesto mensual aplica al mes actual si es repetitivo
    // y su inicio (p.mes) es <= el mes actual, o si es puntual y coincide exactamente.
    const presupuestosMes = presupuestos.filter(p =>
      p.periodicidad === 'mensual' &&
      (p.repetitivo ? p.mes <= mesActualStr : p.mes === mesActualStr),
    );
    return presupuestosMes
      .map(p => {
        const gastado = transacciones
          .filter(t => {
            if (t.tipo !== 'gasto' || t.traspasoId) return false;
            if (t.presupuestoId !== p.id) return false;
            // Solo las transacciones del mes actual
            const f = new Date(t.fecha);
            return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}` === mesActualStr;
          })
          .reduce((s, t) => s + t.cantidad, 0);
        const limite = limiteEnPeriodo(p, mesActualStr);
        const pct = limite > 0 ? (gastado / limite) * 100 : 0;
        return { p, gastado, pct, limite };
      })
      .filter(({ pct }) => pct >= 90)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 4); // máximo 4 alertas
  }, [presupuestos, transacciones, mesActualStr]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-gray-600 dark:text-gray-400">Cargando datos...</div>
      </div>
    );
  }

  const nombreUsuario = nombreDeUsuario(user);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
            Hola, {nombreUsuario} 👋
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Día {diaHoy} de {totalDiasMes} — resumen de tus finanzas
          </p>
        </div>

        <Novedades modulo="finanzas" acciones={{
          'plantilla-categorias': () => setVerPlantilla(true),
          'informes-nuevos': () => navigate('/finanzas/informes'),
          'apuntar-apple-pay': () => setVerApplePay(true),
        }} />
        <AvisoResumenMes />

        {/* ── Alertas ──────────────────────────────────────────────────────── */}
        {(periodVencidos > 0 || alertasPresupuesto.length > 0) && (
          <div className="space-y-2 mb-6">

            {/* Periódicos vencidos */}
            {periodVencidos > 0 && (
              <Link to="/finanzas/periodicos"
                className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/25
                           border border-amber-300 dark:border-amber-700 rounded-xl
                           px-4 py-3 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                <p className="flex-1 text-sm font-semibold text-amber-800 dark:text-amber-300">
                  {periodVencidos} periódico{periodVencidos > 1 ? 's' : ''} pendiente{periodVencidos > 1 ? 's' : ''} de ejecutar
                </p>
                <ChevronRight className="w-4 h-4 text-amber-500 shrink-0" />
              </Link>
            )}

            {/* Presupuestos al límite */}
            {alertasPresupuesto.map(({ p, gastado, pct, limite }) => {
              const excedido = pct > 100;
              return (
                <Link key={p.id} to="/finanzas/presupuestos"
                  className={`flex items-center gap-3 rounded-xl px-4 py-3 transition border ${
                    excedido
                      ? 'bg-red-50 dark:bg-red-900/25 border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40'
                      : 'bg-orange-50 dark:bg-orange-900/25 border-orange-300 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/40'
                  }`}>
                  <AlertTriangle className={`w-5 h-5 shrink-0 ${excedido ? 'text-red-600 dark:text-red-400' : 'text-orange-500 dark:text-orange-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${excedido ? 'text-red-800 dark:text-red-300' : 'text-orange-800 dark:text-orange-300'}`}>
                      {excedido ? '⚠ Excedido' : '🔶 Cerca del límite'}: {p.nombre}
                    </p>
                    <p className={`text-xs ${excedido ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`}>
                      {formatoEuros(gastado)} de {formatoEuros(limite)} ({pct.toFixed(0)} %)
                    </p>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 ${excedido ? 'text-red-400' : 'text-orange-400'}`} />
                </Link>
              );
            })}
          </div>
        )}

        {/* ── 4 Cards de resumen ───────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Saldo</p>
              <div className="bg-blue-100 dark:bg-blue-900 p-1.5 rounded-lg shrink-0">
                <Wallet className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{formatoEuros(saldoTotal)}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ingresos</p>
              <div className="bg-green-100 dark:bg-green-900 p-1.5 rounded-lg shrink-0">
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-green-600">{formatoEuros(ingresosMes)}</p>
            <p className="text-xs text-gray-400 mt-0.5">este mes</p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Gastos</p>
              <div className="bg-red-100 dark:bg-red-900 p-1.5 rounded-lg shrink-0">
                <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-red-600">{formatoEuros(gastosMes)}</p>
            {/* Proyección */}
            {diaHoy > 2 && diaHoy < totalDiasMes && (
              <p className="text-xs text-gray-400 mt-0.5">
                ~{formatoEuros(Math.round(proyeccionMes))} estimado/mes
              </p>
            )}
          </div>

          <div className={`rounded-xl shadow p-4 ${
            ahorroMes >= 0
              ? 'bg-emerald-50 dark:bg-emerald-900/20'
              : 'bg-red-50 dark:bg-red-900/20'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ahorro</p>
              <div className={`p-1.5 rounded-lg shrink-0 ${ahorroMes >= 0 ? 'bg-emerald-100 dark:bg-emerald-900' : 'bg-red-100 dark:bg-red-900'}`}>
                <PiggyBank className={`w-4 h-4 ${ahorroMes >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} />
              </div>
            </div>
            <p className={`text-xl font-bold ${ahorroMes >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatoEuros(ahorroMes, true)}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">este mes</p>
          </div>
        </div>

        {/* ── A medias: lo que apuntaron otros, por pasar ───────────────────── */}
        <AvisosAMedias solo="finanzas" />

        {/* ── Adelantos pendientes ─────────────────────────────────────────── */}
        {pendienteAdelantos.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-3 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <HelpingHand className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Adelantos pendientes</p>
            </div>
            <ul className="space-y-1">
              {pendienteAdelantos.map(({ persona, importe }) => (
                <li key={persona} className="flex items-center gap-3 text-sm text-amber-800 dark:text-amber-300">
                  <span className="flex-1 truncate">{persona}</span>
                  <span className="shrink-0">
                    {importe > 0
                      ? <>te debe <span className="font-bold">{formatoEuros(importe)}</span></>
                      : <>te ha adelantado <span className="font-bold">{formatoEuros((-importe))}</span></>}
                  </span>
                  <button onClick={() => saldar(persona, importe)} disabled={saldando === persona}
                    title="Dar por cerradas las cuentas con esta persona"
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold
                               border border-amber-300 dark:border-amber-600 hover:bg-amber-100
                               dark:hover:bg-amber-900/40 transition disabled:opacity-50">
                    <CheckCheck className="w-3.5 h-3.5" /> Saldar
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Sin cuentas: lo primero es crear una ─────────────────────────── */}
        {sinCuentas && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-6 border-l-4 border-blue-500">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 dark:bg-blue-900/40 p-2 rounded-lg shrink-0">
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-bold text-gray-900 dark:text-white">Empieza creando tu primera cuenta</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  Los gastos e ingresos se apuntan siempre en una cuenta (el banco, el efectivo…).
                  Te guiamos en un minuto.
                </p>
                <button onClick={() => setVerAsistente(true)}
                  className="mt-3 inline-flex items-center gap-2 bg-blue-600 text-white font-semibold py-2.5 px-5 rounded-xl
                             hover:bg-blue-700 active:scale-95 transition">
                  Empezar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Botón acción ────────────────────────────────────────────────── */}
        <div className={`mb-6 ${sinCuentas ? 'hidden' : ''}`}>
          <button onClick={() => setShowModal(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-2
                       bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl
                       hover:bg-blue-700 active:scale-95 transition">
            <PlusCircle className="w-5 h-5" />
            Agregar Transacción
          </button>
        </div>

        {/* ── Gráfico ─────────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
            Gastos por categoría este mes
          </h2>
          <GraficoGastos transacciones={transacciones} categorias={categorias} />
        </div>
      </div>

      {showModal && <ModalAgregarTransaccion onClose={() => setShowModal(false)} />}
      {verApplePay && <GuiaApplePay onClose={() => setVerApplePay(false)} />}
      {asistenteEnCurso && <AsistenteFinanzas onClose={() => cerrarAsistente()} onTerminar={cerrarAsistente} />}
      {ofrecerPlantilla && !showModal && <ModalPlantillaCategorias bienvenida onClose={cerrarPlantilla} />}
      {verPlantilla && !ofrecerPlantilla && <ModalPlantillaCategorias onClose={() => setVerPlantilla(false)} />}
    </div>
  );
};
